import { generateToast } from '@/ui/components/toasting'
import basicShieldIcon from '@assets/images/basic-shield.svg?react'
import DeviceIcon from '@assets/images/asset-device-icon.svg?react'
import DigitalFootprintIcon from '@assets/images/digital-footprint-icon.svg?react'
import UserIcon from '@assets/images/user-icon.svg?react'
import ExternalLinkIcon from '@assets/images/external-link-icon.svg?react'
import IntegrationIcon from '@assets/images/integration-icon.svg?react'
import MDRIcon from '@assets/images/mdr-icon.svg?react'
import ProIcon from '@assets/images/pro-icon.svg?react'
import SecurityAwarenessTrainingIcon from '@assets/images/security-awareness-training.svg?react'
import SummaryIcon from '@assets/images/summary-icon.svg?react'
import { ROUTES } from '@core/config/routing'
import { FC, ReactElement, SVGProps } from 'react'
import { keyErrorInfo } from '../modules/issues/constants'
import { ProductivityIntegrationsModule } from '../modules/productivity-integrations/productivity-integrations.module'
import { testIdByErrorType } from '../modules/productivity-integrations/wizer-toast.constants'
import { ASM_DIALOGS_MAP, DialogService } from '../services/dialog'
import { Analytics } from '@core/services/mixpanel'
import { theme } from '../theme/theme-light'
import { AccountModule } from '../modules/account/account.module'

export type TLink = {
    id: string
    kind?: 'item' | 'separator'
    icon?: FC<SVGProps<SVGSVGElement>>
    route?: TRoute
    text?: string
    isHidden?: boolean
    extraIcon?: ReactElement
    boldIcon?: boolean
    onClick?: () => void
    event?: {
        eventName: AnalyticsEvents
        params?: Record<string, any>
    }
}

type NavigationParams = {
    path: string
    event?: {
        name: AnalyticsEvents
        params?: Record<string, unknown>
    }
}

export const useRouting = () => {
    const location = useLocation()
    const navigate = useNavigate()
    const { isMdrActive, accountType } = AccountModule.useStore()
    const shouldHideSidebar = accountType === 'MDR'
    const isWizerIntegrationActive =
        ProductivityIntegrationsModule.isWizerIntegrationActive()

    const handleSecurityAwarenessTrainingClick = async () => {
        await ProductivityIntegrationsModule.setClicked(true)
        Analytics.event('SEM::user_clicks_at_wizer_security_training')
        if (isWizerIntegrationActive) {
            const { link, error, errorType } =
                await ProductivityIntegrationsModule.getWizerMagicLink()
            if (link) {
                window.open(link, '_blank')
            } else {
                generateToast({
                    type: 'error',
                    data: error ?? keyErrorInfo.generalApiError.message,
                    testId: testIdByErrorType[errorType ?? 'GENERAL_ERROR'],
                })
            }
        } else {
            await DialogService.showDialog(
                ASM_DIALOGS_MAP.WIZER_TRANSITION_SCREEN
            )
        }
    }

    const isActiveLink = (route: TRoute) => {
        if (route?.path === '/') {
            return location?.pathname === route.path
        }

        return location?.pathname?.includes(route?.path)
    }

    const navigateTo = ({ path, event }: NavigationParams): void => {
        if (event) {
            const { name, params } = event

            Analytics.event(name, params)
        }
        navigate(path)
    }

    const handleMyStanceClick = () => {
        navigateTo({
            path: ROUTES.summary.path,
            event: {
                name: 'SEM::navigate_to_summary_page',
                params: {
                    origin: 'sidebar',
                },
            },
        })
    }

    const handleFraudDefenseClick = () => {
        navigateTo({
            path: ROUTES.fraud_defense.path,
            event: {
                name: 'SEM::navigate_to_fraud_defense_page',
                params: {
                    origin: 'sidebar',
                },
            },
        })
    }

    const handleMdrClick = () => {
        navigateTo({
            path: ROUTES.mdr.path,
            event: {
                name: 'SEM::navigate_to_mdr_page',
                params: {
                    origin: 'sidebar',
                    isMdrActive,
                },
            },
        })
    }

    const handleExternalAssetsClick = () => {
        navigateTo({
            path: ROUTES.external_assets.path,
            event: {
                name: 'SEM::navigate_to_external_assets_page',
                params: {
                    origin: 'sidebar',
                    isMdrActive,
                },
            },
        })
    }

    const handleUsersClick = () => {
        navigateTo({
            path: ROUTES.users.path,
            event: {
                name: 'SEM::navigate_to_users_page',
                params: {
                    origin: 'sidebar',
                },
            },
        })
    }

    const handleLogout = (origin = 'sidebar') => {
        navigateTo({
            path: ROUTES.auth.children.logout.path,
            event: {
                name: 'SEM::navigate_to_logout',
                params: { origin },
            },
        })
    }

    const handleDevicesClick = () => {
        navigateTo({
            path: ROUTES.devices.path,
            event: {
                name: 'SEM::navigate_to_devices_page',
                params: {
                    origin: 'sidebar',
                },
            },
        })
    }

    const handleIntegrationsClick = () => {
        navigateTo({
            path: ROUTES.integrations.path,
            event: {
                name: 'SEM::navigate_to_integrations_page',
                params: {
                    origin: 'sidebar',
                },
            },
        })
    }

    const sideNavbarLinks: TLink[] = [
        {
            id: ROUTES.summary.name,
            route: ROUTES.summary,
            icon: SummaryIcon,
            onClick: handleMyStanceClick,
        },
        {
            id: ROUTES.fraud_defense.name,
            route: ROUTES.fraud_defense,
            icon: basicShieldIcon,
            boldIcon: true,
            onClick: handleFraudDefenseClick,
        },
        {
            id: ROUTES.mdr.name,
            route: ROUTES.mdr,
            icon: MDRIcon,
            extraIcon: <ProIcon color={theme.colors.primary[50]} />,
            onClick: handleMdrClick,
            boldIcon: true,
        },
        {
            id: ROUTES.integrations.name,
            route: ROUTES.integrations,
            icon: IntegrationIcon,
            onClick: handleIntegrationsClick,
        },
        {
            id: 'security-training',
            icon: SecurityAwarenessTrainingIcon,
            text: 'Security Training',
            onClick: handleSecurityAwarenessTrainingClick,
            extraIcon: <ExternalLinkIcon width={14} height={14} />,
        },
        {
            id: 'attack-surface-separator',
            kind: 'separator',
            text: 'Attack Surface',
        },
        {
            id: ROUTES.external_assets.name,
            route: ROUTES.external_assets,
            icon: DigitalFootprintIcon,
            onClick: handleExternalAssetsClick,
        },
        {
            id: ROUTES.devices.name,
            route: ROUTES.devices,
            icon: DeviceIcon,
            onClick: handleDevicesClick,
        },
        {
            id: ROUTES.users.name,
            route: ROUTES.users,
            icon: UserIcon,
            onClick: handleUsersClick,
        },
    ]

    return {
        location,
        ROUTES,
        navigate,
        navigateTo,
        isActiveLink,
        sideNavbarLinks,
        handleSecurityAwarenessTrainingClick,
        handleLogout,
        shouldHideSidebar,
    }
}
